/**
 * Сделка рядом с диалогом (экран «Чаты»). Сверху — «Контакты» (`Contacts`): номера клиента —
 * привязанные сотрудником руками (`POST /api/chats/{id}/phones`) и найденные в его сообщениях;
 * первый — активный, по нему `GET /api/search` подбирает активную сделку или лид — так же, как
 * «Поиск»; там же — по каким каналам с клиентом есть связь.
 * Сделки по номеру нет — форма «создать» (`POST /api/chats/{id}/deal`, боевой Bitrix); выбранная
 * сделка закрыта (отказ, «успешна») — над ней кнопка «Создать сделку» с той же формой.
 * Карточка сделки сверху вниз: стадия крупно → сразу под ней переключатель стадий
 * «В работе / Ждем клиента / Думают / Отказ…» (`POST /api/deals/{id}/stage`), второй строкой —
 * город сделки коротко, клик меняет (`POST /api/deals/{id}/location`) → дата записи / следующей
 * обработки, правка сразу (`POST /api/deals/{id}/wait`) → мероприятие и его дата
 * (`POST /api/deals/{id}/event`) → даты проката
 * (крупно для «Забронировано» и «Ожидаем возврат», правка — `POST /api/deals/{id}/rent`) →
 * большая кнопка «Открыть в Bitrix» → номер сделки, приходы в салон и выдача одной строкой.
 * Соседние вкладки панели: «Договор» (`ContractTab`, у любой сделки; там же повторная
 * примерка — `VisitSection`), «История»
 * (хронология новыми сверху) и «ЛК» — журнал действий сотрудников в кабинете (`user_action`,
 * `GET /api/chats/{id}/actions`): каждое действие в панели пишется в него — и удачное, и
 * отбитое ошибкой.
 * Кандидатов несколько — переключатель.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, ExternalLink, Plus } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { describeAction } from "@/components/chat/actionText";
import { BitrixPending } from "@/components/chat/BitrixPending";
import { Contacts, fmtPhone } from "@/components/chat/Contacts";
import { ContractTab } from "@/components/chat/ContractTab";
import { DeliveryTab } from "@/components/chat/DeliveryTab";
import { VisitBlock } from "@/components/chat/VisitBlock";
import { Combo } from "@/components/Combo";
import { DatePick, DateTimePick, dateTimeLabel } from "@/components/DateTimePick";
import { Timeline, dt } from "@/components/timeline/Timeline";
import { SlotTime } from "@/components/zapis/SlotTime";
import { bitrixUrl } from "@/lib/calls";
import {
  attachPhone,
  type ChatActiveDeal,
  type ChatDealInfo,
  type ChatLeadInfo,
  type ChatPhone,
  detachPhone,
  resolveChat,
} from "@/lib/clientChats";
import { type CardEvent, type DealCard, loadDealCard } from "@/lib/dealCard";
import { dealHasPhone } from "@/lib/dealPhones";
import {
  type DealEvent,
  type Location,
  type Stage,
  type StageKind,
  addDealComment,
  createDealFromChat,
  createDealByPhone,
  createDealFromLead,
  day,
  loadChatActions,
  loadStages,
  logChatAction,
  sendZapisLink,
  rentDays,
  setDealEvent,
  setDealLocation,
  setDealRent,
  setDealStage,
  setDealWait,
  toLocalInput,
  zapisLinkText,
} from "@/lib/dealStage";
import { loadLeadCard } from "@/lib/leadCard";
import { loadLeadNextWork, loadLeadStatuses, setLeadStatus } from "@/lib/leadStatus";
import { btn as pbtn } from "@/lib/panelBtn";
import { loadSearch } from "@/lib/search";
import { NavMenu, navObject, navUrl } from "@/nav/NavMenu";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const note = "p-4 text-[12.5px] text-fg-muted";
const chip = "rounded-[var(--radius-ui)] border px-2 py-0.5 text-[12px]";
const on = "border-accent text-fg";
const off = "border-border text-fg-muted hover:text-fg";
const input = "h-7 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[12px]";
const btn = "h-7 rounded-[var(--radius-ui)] border border-accent px-2 text-[12px] text-accent-text hover:bg-bg-hover disabled:opacity-50";
const ghost = "h-7 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg-muted hover:bg-bg-hover disabled:opacity-50";
/** Тесный вид (`compact`) — панель расширения 260–320 px: подписи мелко над полями, стадии
 * короткими названиями CRM, без внешних отступов (их даёт родитель). «Чаты» — вид по умолчанию. */
const caption = "mb-1 block text-[11px] leading-none text-fg-muted";
const stageName = (s: Stage, compact: boolean) => (compact ? s.short || s.name : s.name);
/** Правка дошла до Bitrix — говорим это прямо. Легаси-окошки CRM узнают о ней с задержкой (03.10
 * дата записи дошла до CRM через 26 с): без подтверждения сотрудник записывал повторно. */
function Saved({ at, className = "" }: { at: number; className?: string }) {
  return (
    <div role="status" className={`mt-1 rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1 leading-snug text-ok ${className}`} data-annot="записано в Bitrix">
      ✓ Записано в Bitrix в {new Date(at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}. Окошки CRM обновятся в течение
      минуты — повторять не нужно.
    </div>
  );
}

/** Стадии, в которые сотрудник переставляет сделку сам; отказы — отдельным списком. */
const KC: StageKind[] = ["work", "wait", "think"];
const STAGE_COLOR: Record<StageKind, string> = {
  work: "text-accent-text",
  wait: "text-warn",
  think: "text-fg",
  refuse: "text-err",
  won: "text-ok",
  other: "text-fg",
};
/** «Забронировано» и «Ожидаем возврат» (платье у клиента) — даты проката крупно. */
const RENT_STAGES = new Set(["4", "2"]);
/** Лид «Не обработан» / «В работе» сервер конвертирует в создаваемую сделку (`deal/create`). */
const CONVERTIBLE = new Set(["NEW", "IN_PROCESS"]);
/** Карточку сделки и лида (0,5–3 с) на каждое открытие чата не перечитываем: минуту живёт копия,
 * правки панели сбрасывают её ключ сами (аудит «Чатов» 21.10.26; панель расширения — `КАРТОЧКА_ЖИВЁТ`). */
export const DEAL_CARD_STALE = 60_000;

type Obj = { entity: "deal" | "lead"; id: number; hint: string };
/** Вкладка панели «Чатов», которую сейчас показывает `DealPanel`: сделка, её договор, история
 * или «ЛК» — действия сотрудников в кабинете. */
export type PanelView = "deal" | "contract" | "delivery" | "history" | "lk";

/** Имя чата WhatsApp — это номер; в имя контакта такое не подставляем. */
const looksLikePhone = (s: string) => /^[\d\s()+\-]+$/.test(s.trim());

const useStages = () => useQuery({ queryKey: ["deal-stages"], queryFn: loadStages, staleTime: 60 * 60_000 });

const SHORT_MONTHS = "янв фев мар апр мая июн июл авг сен окт ноя дек".split(" ");
const shortDay = (iso: string) => `${Number(iso.slice(8, 10))} ${SHORT_MONTHS[Number(iso.slice(5, 7)) - 1] ?? ""}`;
/** Подпись сделки в переключателе: номер договора из названия «Договор 93295/1258266», стадия,
 * даты проката — то же, что видно во вкладке «Договор», чтобы понять, какую сделку смотрим. */
const activeLabel = (d: ChatActiveDeal) =>
  [
    (n => (n ? `дог. ${n}` : null))(/Договор\s+(\d+)/.exec(d.title)?.[1]),
    d.stage,
    d.rent_start ? `${shortDay(d.rent_start)}${d.rent_end ? `–${shortDay(d.rent_end)}` : ""}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

export function DealPanel({
  chatId,
  name,
  deals,
  dealsActive = [],
  leads,
  phones,
  cached,
  view = "deal",
  onDraft,
  onOffer,
  onHasDeal,
}: {
  chatId: number;
  name: string;
  deals: number[];
  /** Все сделки клиента в работе (своя копия Bitrix) — в переключатель, видно на всех вкладках. */
  dealsActive?: ChatActiveDeal[];
  leads: number[];
  phones: ChatPhone[];
  /** Сделка/лид из кэша сервера (своя копия Bitrix) и видел ли портал активный номер. */
  cached: { deal: ChatDealInfo | null; lead: ChatLeadInfo | null; phone: string | null; phoneChecked: boolean };
  /** Вкладка панели: «Сделка» или «История». Панель смонтирована на обеих — выбранная сделка,
   *  недописанный комментарий и открытое «Время записи» переключение переживают. */
  view?: PanelView;
  /** Положить текст в поле ответа чата (окно «Время записи»); нет — кнопки окна нет. */
  onDraft?: (text: string) => void;
  /** Положить текст и файлы в поле ответа («фото в чат» во вкладке «Договор»). */
  onOffer?: (text: string, files: File[]) => void;
  /** Есть ли у диалога сделка — от неё зависит вкладка «Договор» (создать договор можно в любой). */
  onHasDeal?: (has: boolean) => void;
}) {
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  // Лид уже стал сделкой: живая карточка лида (Bitrix + CRM) знает это раньше своей копии.
  const [converted, setConverted] = useState<{ chat: number; deal: number } | null>(null);
  const viaLead = converted?.chat === chatId ? converted.deal : null;
  // Форма новой сделки поверх закрытой — открыта кнопкой; чат сменили — закрыта.
  const [creatingIn, setCreatingIn] = useState<number | null>(null);
  const creating = creatingIn === chatId;
  const active = chosen && phones.some((p) => p.phone === chosen) ? chosen : (phones[0]?.phone ?? null);
  // Сделка — из кэша сервера (`cached`, своя копия Bitrix). Живой Bitrix — только для номера,
  // которого портал ещё не видел (копия по нему пуста), или кнопкой «проверить в Bitrix».
  const search = useQuery({
    queryKey: ["search", active],
    queryFn: () => loadSearch(active ?? ""),
    enabled: active !== null && !cached.phoneChecked,
    staleTime: 5 * 60_000,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
    void qc.invalidateQueries({ queryKey: ["chat-contacts", chatId] });
  };
  const found = active !== null ? search.data : undefined;
  // После живого поиска кэш на сервере пополнился — пересчитать привязку, список увидит сделку.
  useEffect(() => {
    if (!found) return;
    void resolveChat(chatId).then(
      () => {
        void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
        void qc.invalidateQueries({ queryKey: ["client-chats"] });
        void qc.invalidateQueries({ queryKey: ["chat-contacts", chatId] });
      },
      () => undefined,
    );
  }, [found, chatId, qc]);
  // Кэш привязки отстал от Bitrix (чат на лиде, а сделка уже есть) — пересчитать сейчас.
  useEffect(() => {
    if (viaLead === null || cached.deal?.id === viaLead) return;
    void resolveChat(chatId).then(
      () => {
        void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
        void qc.invalidateQueries({ queryKey: ["client-chats"] });
        void qc.invalidateQueries({ queryKey: ["chat-contacts", chatId] });
      },
      () => undefined,
    );
  }, [viaLead, cached.deal?.id, chatId, qc]);
  // Сделка чата не из его номеров: один раз пересчитать привязку; не сошлось — предупреждение ниже.
  const chatPhones = [...(cached.phone ? [cached.phone] : []), ...phones.map((p) => p.phone)];
  const foreign = !dealHasPhone(cached.deal, chatPhones) ? cached.deal : null;
  const asked = useRef("");
  useEffect(() => {
    const was = `${chatId}:${foreign?.id ?? ""}`;
    if (!foreign || asked.current === was) return;
    asked.current = was;
    void resolveChat(chatId).then(
      () => void qc.invalidateQueries({ queryKey: ["client-chat", chatId] }),
      () => undefined,
    );
  }, [foreign, chatId, qc]);
  const attach = useMutation({
    mutationFn: (phone: string) => attachPhone(chatId, phone),
    onSuccess: (r) => {
      setChosen(r.phones[0]?.phone ?? null); // привязанный встаёт первым и становится активным
      refresh();
    },
  });
  const detach = useMutation({ mutationFn: (phone: string) => detachPhone(chatId, phone), onSuccess: refresh });

  const main = found?.main ?? null;
  const objects: Obj[] = [];
  const add = (o: Obj) => {
    if (!objects.some((x) => x.entity === o.entity && x.id === o.id)) objects.push(o);
  };
  if (cached.deal) add({ entity: "deal", id: cached.deal.id, hint: `${cached.deal.stage} · по своей базе` });
  if (!cached.deal && cached.lead) add({ entity: "lead", id: cached.lead.id, hint: `${cached.lead.status} · по своей базе` });
  dealsActive.forEach((d) => add({ entity: "deal", id: d.id, hint: d.title || d.stage }));
  if (main) add({ entity: main.entity, id: main.id, hint: main.reason });
  deals.forEach((id) => add({ entity: "deal", id, hint: "по ссылке брони или линии Bitrix" }));
  found?.deals.forEach((d) =>
    add({ entity: "deal", id: d.id, hint: `${d.active ? "в работе" : "закрыта"} · ${d.stage_name ?? "—"}` }),
  );
  leads.forEach((id) => add({ entity: "lead", id, hint: "по линии Bitrix" }));
  found?.leads.forEach((l) =>
    add({ entity: "lead", id: l.id, hint: `${l.active ? "в работе" : "закрыт"} · ${l.status_name}` }),
  );
  const current = objects.find((o) => `${o.entity}:${o.id}` === picked) ?? objects[0] ?? null;
  // Салон и день записи выбранной сделки — с чего открыть «Время записи»; та же карточка, что ниже (кэш).
  const dealId = current?.entity === "deal" ? current.id : null;
  // Карточка — минуту: правки панели сбрасывают её ключ сами (аудит «Чатов» 21.10.26).
  const dealCard = useQuery({
    queryKey: ["deal-card", dealId],
    queryFn: () => loadDealCard(dealId!),
    enabled: dealId !== null,
    staleTime: DEAL_CARD_STALE,
  });
  const stageSet = useStages();
  const deal = dealId !== null ? dealCard.data?.deal : undefined;
  const hasDeal = dealId !== null;
  useEffect(() => onHasDeal?.(hasDeal), [hasDeal, onHasDeal]);
  const searching = active !== null && search.isFetching;
  const failure = attach.error ?? detach.error;
  // По номеру сделки нет (в кэше пусто, живой поиск — если был — завершён) — предлагаем создать, даже если есть лид.
  const settled = cached.phoneChecked ? !searching : !!found;
  const noDeal =
    active !== null &&
    settled &&
    !objects.some((o) => o.entity === "deal") &&
    !(current?.entity === "lead" && viaLead !== null); // под лидом уже открыта его сделка
  // Сделка закрыта (отказ или «успешна») — клиент вернулся: нужна новая. Стадия — из живой карточки,
  // до неё — из кэша; пока неизвестна, считаем сделку активной: кнопка не должна мигнуть.
  const currentKind =
    dealId === null
      ? undefined
      : deal
        ? stageSet.data?.stages.find((x) => x.code === deal.stage_code)?.kind
        : cached.deal?.id === dealId
          ? cached.deal.kind
          : undefined;
  const closedDeal = active !== null && (currentKind === "refuse" || currentKind === "won");

  // Кнопки записи стоят ПОД блоком сделки (лида), как в панели расширения.
  const zapis = (
    <ZapisLink
      chatId={chatId}
      locationId={(stageSet.data?.locations ?? []).find((l) => l.bitrix_id === deal?.location_bitrix_id)?.id ?? null}
      // «Ждем клиента» — `visit` это запись; в других стадиях — дата обработки, окну она не нужна.
      day={(stageSet.data?.stages.find((x) => x.code === deal?.stage_code)?.kind === "wait" && deal?.visit?.slice(0, 10)) || null}
      onDraft={onDraft}
    />
  );
  return (
    <>
      <div hidden={view !== "deal"} className="flex flex-col gap-2 px-3 pt-3">
        <Contacts
          chatId={chatId}
          phones={phones}
          active={active}
          onPick={setChosen}
          onAttach={(phone) => attach.mutate(phone)}
          onDetach={(phone) => detach.mutate(phone)}
          busy={attach.isPending || detach.isPending}
          actions={
            active && (
              <>
                <button
                  type="button"
                  onClick={() => void search.refetch()}
                  disabled={searching}
                  title="Живой поиск по номеру в Bitrix и CRM; обычно сделка берётся из своей базы"
                  className="text-[12px] text-accent-text hover:underline disabled:opacity-50"
                  data-annot="проверить в Bitrix"
                >
                  {searching ? "проверяю…" : "проверить в Bitrix"}
                </button>
                <Link to={`/search?q=${active}`} className="text-[12px] text-accent-text hover:underline">
                  поиск →
                </Link>
              </>
            )
          }
          footer={
            <>
              {failure && <div className="mt-1 text-err">{failure.message}</div>}
              {foreign && (
                <div className="mt-1 text-warn" data-annot="сделка не с номера чата">
                  В сделке {foreign.id} нет номера этого чата — проверьте, тот ли клиент. Номера сделки: {foreign.phones?.map(fmtPhone).join(", ")}
                </div>
              )}
              {searching && <div className="mt-1 text-fg-muted">Ищу сделку по номеру {fmtPhone(active ?? "")}…</div>}
              {active !== null && search.isError && (
                <div className="mt-1 text-err">Поиск по номеру не удался: {search.error.message}</div>
              )}
              {found && <div className="mt-1 text-fg-muted">{main ? main.reason : "По номеру ни сделок, ни лидов."}</div>}
              {found && found.errors.length > 0 && (
                <div className="mt-1 text-warn">Поиск неполный: {found.errors.join("; ")}</div>
              )}
            </>
          }
        />
        {closedDeal &&
          active &&
          (creating ? (
            <CreateDeal
              chatId={chatId}
              phone={active}
              name={name}
              lead={cached.lead && CONVERTIBLE.has(cached.lead.status_code) ? cached.lead.id : null}
              title={`Новая сделка — сделка ${dealId} закрыта`}
              onCancel={() => setCreatingIn(null)}
              onDone={() => {
                setCreatingIn(null);
                setPicked(null); // новая сделка встаёт первой — показываем её, а не закрытую
                void qc.invalidateQueries({ queryKey: ["client-chats"] });
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setCreatingIn(chatId)}
              title={`Сделка ${dealId} закрыта — завести клиенту новую в «Прокате» (Bitrix)`}
              className={`${pbtn.outline} w-full min-w-0`}
              data-annot="создать сделку"
            >
              <Plus size={15} aria-hidden /> Создать сделку
            </button>
          ))}
        {noDeal && active && (
          <CreateDeal
            chatId={chatId}
            phone={active}
            name={name}
            lead={
              found?.leads.find((l) => CONVERTIBLE.has(l.status))?.id ??
              (cached.lead && CONVERTIBLE.has(cached.lead.status_code) ? cached.lead.id : null)
            }
          />
        )}
      </div>
      {/* Переключатель — на всех вкладках: «Договор» и «Доставка» показывают выбранную здесь сделку. */}
      {objects.length > 1 && (
        <div className="flex flex-col gap-1 px-3 pt-2" data-annot="переключатель сделок">
          <div className="flex flex-wrap gap-1">
            {objects.map((o) => {
              const key = `${o.entity}:${o.id}`;
              const isOn = current !== null && key === `${current.entity}:${current.id}`;
              const act = o.entity === "deal" ? dealsActive.find((d) => d.id === o.id) : undefined;
              return (
                <button key={key} type="button" onClick={() => setPicked(key)} title={o.hint} className={`${chip} ${isOn ? on : off}`}>
                  {o.entity === "deal" ? "Сделка" : "Лид"} <span className="font-mono">{o.id}</span>
                  {act && <span className="text-fg-muted"> · {activeLabel(act)}</span>}
                </button>
              );
            })}
          </div>
          {dealsActive.length > 1 && (
            <div className="text-[12px] text-warn">
              Сделок в работе у клиента: {dealsActive.length} — проверьте, не дубль ли. Лишнюю выберите здесь и
              переведите в «Отказ» на вкладке «Сделка».
            </div>
          )}
        </div>
      )}
      {current?.entity === "deal" && <Deal key={current.id} dealId={current.id} chatId={chatId} view={view} slot={zapis} />}
      {current?.entity === "lead" && (
        <Lead key={current.id} leadId={current.id} chatId={chatId} view={view} slot={zapis} onDeal={(deal) => setConverted({ chat: chatId, deal })} />
      )}
      {!current && (
        <div hidden={view !== "deal"} className="p-3">
          {zapis}
        </div>
      )}
      {!current && view === "history" && <div className={note}>Истории нет: у диалога нет ни сделки, ни лида.</div>}
      {view === "contract" &&
        (dealId !== null ? <ContractTab key={dealId} dealId={dealId} chatId={chatId} onOffer={onOffer} phone={active} name={name} /> : <div className={note}>Договора нет: у диалога нет сделки.</div>)}
      {view === "delivery" &&
        (dealId !== null ? <DeliveryTab dealId={dealId} onOffer={onOffer} /> : <div className={note}>Доставки нет: у диалога нет сделки.</div>)}
      {view === "lk" && (
        <div className="p-3">
          <Actions chatId={chatId} dealId={dealId} stages={stageSet.data?.stages ?? []} />
        </div>
      )}
      {!current && view === "deal" && !searching && !noDeal && (
        <div className={note}>
          Сделки у этого диалога нет: клиент пришёл не по ссылке брони, в Bitrix диалог ни к лиду, ни к сделке не
          привязан, а номера клиента нет — привяжите его выше.
        </div>
      )}
    </>
  );
}

/** Клиент не знает, когда прийти: шлём ему в чат ссылку `my.uni-renter.ru/z/<хэш>` — день и час
 * он выберет сам, видя загрузку салона; сделка встанет в «Ждем клиента» (или создастся новая).
 * Пишет клиенту на боевую площадку. «Время записи» рядом раскрывает загрузку салона по часам
 * (`SlotTime`): текст со ссылкой и рекомендованным временем встаёт в поле ответа, клиенту не уходит. */
function ZapisLink({
  chatId,
  locationId,
  day,
  onDraft,
}: {
  chatId: number;
  /** Салон и день записи сделки чата — с чего открыть «Время записи». */
  locationId: number | null;
  day: string | null;
  onDraft?: ((text: string) => void) | undefined;
}) {
  const qc = useQueryClient();
  const [time, setTime] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
    void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
  };
  const send = useMutation({ mutationFn: () => sendZapisLink(chatId), onSettled: refresh });
  // Две кнопки в ряд, как в панели расширения: главная — ссылка, рядом — «Время записи».
  return (
    <div className="flex flex-col gap-1 text-[12.5px]" data-annot="ссылка на запись">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => send.mutate()}
          disabled={send.isPending}
          title="Сразу уходит клиенту: он сам выберет день и час примерки, по ссылке видна загрузка салона по часам"
          className={`${pbtn.primary} min-w-0 flex-[1_1_150px]`}
        >
          {send.isPending ? "Отправляю…" : "Отправить ссылку на запись"}
        </button>
        {onDraft && (
          <button
            type="button"
            onClick={() => setTime((v) => !v)}
            aria-expanded={time}
            title="Загрузка салона по часам и готовый текст клиенту с удобным временем — в поле ответа"
            className={`${pbtn.outline} min-w-0 flex-[1_1_150px] ${time ? "bg-accent-dim" : ""}`}
            data-annot="время записи"
          >
            <Clock size={15} aria-hidden /> Время записи
          </button>
        )}
      </div>
      {send.isSuccess && (
        <p role="status" className="rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1.5 leading-snug text-ok">
          Ссылка ушла клиенту: <span className="break-all font-mono text-[12px]">{send.data.url}</span>
        </p>
      )}
      {send.isError && <p role="alert" className="rounded-[var(--radius-ui)] bg-err-bg px-2 py-1.5 leading-snug text-err">{send.error.message}</p>}
      {time && onDraft && (
        <div className={`${card} mt-1`}>
          <SlotTime
            key={chatId}
            wide
            locationId={locationId}
            day={day}
            actionLabel="В поле ответа"
            onInsert={async (location, picked) => {
              const r = await zapisLinkText(chatId, location, picked);
              onDraft(r.text);
              refresh();
              return "Текст — в поле ответа. Проверьте и отправьте.";
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Сделки по номеру нет: создать в «Прокате» или «Приёме платьев» (переключатель направления,
 * у каждого свои стадии из `/api/deals/stages` → `directions`) — стадия, дата (календарь + время с клавиатуры),
 * салон (список с поиском), имя. Лид «Не обработан» / «В работе» по номеру сервер конвертирует
 * в эту сделку. Без чата (`chatId` = null, панель расширения в Bitrix) сделку создаём из лида
 * открытой карточки `fromLead`. Пишет в боевой Bitrix. */
export function CreateDeal({
  chatId,
  fromLead,
  fromCall,
  phone,
  name,
  lead,
  compact = false,
  title = "Сделки по номеру нет — создать",
  onDone,
  onCancel,
}: {
  chatId: number | null;
  /** Лид открытой карточки Bitrix: чата нет — сделку создаём по нему. */
  fromLead?: number | undefined;
  /** Карточка номера в «Чатах»: ни чата, ни лида — сделку по номеру (`bitrix_call.id` или 0). */
  fromCall?: number | undefined;
  phone: string;
  name: string;
  /** Лид «Не обработан» / «В работе» по номеру — он станет этой сделкой. */
  lead: number | null;
  /** Панель расширения (260–320 px): подписи над полями, салон — обычным списком. */
  compact?: boolean;
  title?: string;
  /** Сделка создана — панель расширения переспрашивает, кто открыт, и убирает форму. */
  onDone?: (result: Awaited<ReturnType<typeof createDealFromChat>>) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const stages = useStages();
  const [category, setCategory] = useState(0);
  const [stage, setStage] = useState("PREPAYMENT_INVOICE");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  // Имя чата WhatsApp — номер телефона: тогда поле пустое, и контакт создастся без имени.
  const [who, setWho] = useState(looksLikePhone(name) ? "" : name);
  const create = useMutation({
    mutationFn: () => {
      const body = { name: who.trim(), stage, category, wait_at: date || null, location: location ? Number(location) : null };
      if (chatId) return createDealFromChat(chatId, { phone, ...body });
      return fromLead ? createDealFromLead(fromLead, body) : createDealByPhone(fromCall ?? 0, { phone, ...body });
    },
    onSuccess: (result) => {
      if (chatId) {
        void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
        void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      } else if (fromLead) void qc.invalidateQueries({ queryKey: ["lead-card", fromLead] });
      void qc.invalidateQueries({ queryKey: ["search", phone] });
      void qc.invalidateQueries({ queryKey: ["quick-jump", phone] }); // карточка номера в «Чатах»
      onDone?.(result);
    },
  });
  const directions = stages.data?.directions ?? [];
  const direction = directions.find((d) => d.id === category);
  const opts = direction?.stages ?? [];
  const isWait = opts.find((s) => s.code === stage)?.kind === "wait";
  const uid = chatId ?? (fromLead ? `lead-${fromLead}` : `phone-${phone}`);
  const salons = [
    { value: "", label: "не выбран" },
    ...(stages.data?.locations ?? []).map((l) => ({ value: String(l.id), label: l.name, title: l.short })),
  ];
  return (
    <form
      className={compact ? "flex flex-col rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2 text-[12.5px]" : card}
      data-annot="создать сделку"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="mb-1 flex items-baseline justify-between gap-2 text-[13px] font-medium">
        {title}
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-[12px] font-normal text-fg-muted hover:text-fg hover:underline">
            отмена
          </button>
        )}
      </div>
      {lead !== null && (
        <div className="mb-1 text-fg-muted">
          Лид <span className="font-mono">{lead}</span> в работе — станет этой сделкой (в Bitrix уйдёт в «Качественный лид»).
        </div>
      )}
      {directions.length > 1 && (
        <div className="mb-1.5 flex flex-wrap gap-1" role="group" aria-label="Направление новой сделки" data-annot="направление сделки">
          {directions.map((d) => (
            <button
              key={d.id}
              type="button"
              aria-pressed={d.id === category}
              onClick={() => {
                setCategory(d.id);
                setStage(d.default ?? "");
              }}
              className={`${chip} ${d.id === category ? on : off}`}
            >
              {d.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1" role="group" aria-label="Стадия новой сделки">
        {opts.map((s) => (
          <button key={s.code} type="button" aria-pressed={s.code === stage} onClick={() => setStage(s.code)} className={`${chip} ${s.code === stage ? on : off}`}>
            {stageName(s, compact)}
          </button>
        ))}
      </div>
      <div className="mt-2">
        <DateTimePick
          label={isWait ? "Дата записи" : "Следующая обработка"}
          value={date}
          onChange={setDate}
          required={isWait}
          placeholder={isWait ? "день и время записи" : "не задана"}
          compact={compact}
        />
      </div>
      {compact ? (
        <div className="mt-2 grid grid-cols-2 gap-x-1.5">
          <label className={caption} htmlFor={`new-deal-salon-${uid}`}>салон</label>
          <label className={caption} htmlFor={`new-deal-name-${uid}`}>имя клиента</label>
          <select id={`new-deal-salon-${uid}`} value={location} onChange={(e) => setLocation(e.target.value)} className={`${input} w-full min-w-0 bg-bg ${location ? "" : "text-fg-muted"}`}>
            {salons.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input id={`new-deal-name-${uid}`} value={who} onChange={(e) => setWho(e.target.value)} placeholder="без имени" className={`${input} w-full min-w-0`} />
        </div>
      ) : (
        <>
          <div className="mt-2">
            <Combo label="Салон" value={location} options={salons} onChange={setLocation} placeholder="город или салон" width={392} />
          </div>
          <label className="mt-2 block text-fg-muted">
            Имя клиента
            <input
              value={who}
              onChange={(e) => setWho(e.target.value)}
              placeholder="без имени"
              className={`${input} mt-0.5 w-full`}
            />
          </label>
        </>
      )}
      <button type="submit" disabled={create.isPending || opts.length === 0 || (isWait && !date)} className={compact ? `${pbtn.primary} mt-2 w-full min-w-0` : `${btn} mt-2`}>
        <span className="truncate">Создать сделку на {fmtPhone(phone)}</span>
      </button>
      {isWait && !date && compact && <div className="mt-1 text-fg-muted">Для «Ждем клиента» выберите дату записи.</div>}
      {create.isPending && <div className="mt-1 text-fg-muted">Создаю в Bitrix…</div>}
      {create.isError && <div className="mt-1 text-err">{create.error.message}</div>}
      {create.isSuccess && create.data.pending && (
        <div className="mt-1 text-warn">Bitrix не ответил — сделку создадим сами, повтор каждые 20 секунд.</div>
      )}
      {(chatId || fromLead) && (
        <div className="mt-1 empty:hidden">
          <BitrixPending
            chat={chatId}
            lead={fromLead}
            refreshKey={create.isSuccess ? create.submittedAt : 0}
            onDone={() => {
              if (chatId) void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
              if (fromLead) void qc.invalidateQueries({ queryKey: ["lead-card", fromLead] });
              void qc.invalidateQueries({ queryKey: ["search", phone] });
              void qc.invalidateQueries({ queryKey: ["client-chats"] });
              void qc.invalidateQueries({ queryKey: ["ext-peer"] }); // панель расширения: сделка появилась
            }}
          />
        </div>
      )}
      {create.isSuccess && !create.data.pending && create.data.lead_id !== null && !create.data.lead_converted && (
        <div className="mt-1 text-warn">
          Сделка {create.data.id} создана, но лид {create.data.lead_id} не закрылся: {create.data.lead_error || "Bitrix не ответил"}
        </div>
      )}
    </form>
  );
}

/** Переключатель стадий сразу под стадией: КЦ-стадии с датой, «Отказ» раскрывает все отказные. */
export function StageSwitch({
  deal,
  stages,
  chatId,
  compact = false,
}: {
  deal: DealCard["deal"];
  stages: Stage[];
  chatId: number;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const [pick, setPick] = useState<Stage | null>(null);
  const [date, setDate] = useState("");
  const [refuse, setRefuse] = useState(false);
  const change = useMutation({
    mutationFn: (p: { stage: string; wait_at: string | null }) => setDealStage(deal.id, p.stage, p.wait_at, chatId),
    onSuccess: () => {
      setPick(null);
      setRefuse(false);
      void qc.invalidateQueries({ queryKey: ["deal-card", deal.id] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      void qc.invalidateQueries({ queryKey: ["search"] });
    },
  });
  const kc = stages.filter((s) => KC.includes(s.kind));
  const refusals = stages.filter((s) => s.kind === "refuse");
  const inRefuse = refusals.some((s) => s.code === deal.stage_code);
  const pickLabel = pick?.kind === "wait" ? "Дата записи" : "Следующая обработка";
  return (
    <div className={compact ? "" : "mt-2"} data-annot="смена стадии">
      <div className="flex flex-wrap gap-1">
        {kc.map((s) => (
          <button
            key={s.code}
            type="button"
            disabled={change.isPending}
            title={compact ? s.name : undefined}
            onClick={() => {
              setRefuse(false);
              setPick(s);
              setDate(toLocalInput(deal.visit));
            }}
            className={`${chip} ${s.code === deal.stage_code ? on : off}`}
          >
            {stageName(s, compact)}
          </button>
        ))}
        <button
          type="button"
          disabled={change.isPending}
          onClick={() => {
            setPick(null);
            setRefuse(!refuse);
          }}
          className={`${chip} ${inRefuse ? on : off}`}
        >
          Отказ {refuse ? "▴" : "▾"}
        </button>
      </div>
      {refuse && (
        <div className="mt-1 flex flex-wrap gap-1">
          {refusals.map((s) => (
            <button
              key={s.code}
              type="button"
              disabled={change.isPending || s.code === deal.stage_code}
              title={compact ? s.name : undefined}
              onClick={() => change.mutate({ stage: s.code, wait_at: null })}
              className={`${chip} ${s.code === deal.stage_code ? on : "border-border text-err hover:bg-err-bg"}`}
            >
              {stageName(s, compact)}
            </button>
          ))}
        </div>
      )}
      {pick && (
        <form
          className="mt-1 flex flex-wrap items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate({ stage: pick.code, wait_at: date || null });
          }}
        >
          {/* Тесно: дата во всю ширину, кнопки — строкой под ней. */}
          <div className={compact ? "basis-full" : "min-w-[220px] flex-1"}>
            <DateTimePick
              label={compact ? pickLabel.toLowerCase() : pickLabel}
              value={date}
              onChange={setDate}
              required={pick.kind === "wait"}
              placeholder={pick.kind === "wait" ? "день и время записи" : "не задана"}
              compact={compact}
            />
          </div>
          <button type="submit" disabled={change.isPending || (pick.kind === "wait" && !date)} className={btn}>
            → {stageName(pick, compact)}
          </button>
          <button type="button" onClick={() => setPick(null)} className={ghost}>
            отмена
          </button>
        </form>
      )}
      {change.isPending && <div className="mt-1 text-fg-muted">Записываю в Bitrix…</div>}
      {change.isError && <div className="mt-1 text-err">{change.error.message}</div>}
      {change.isSuccess && <Saved at={change.submittedAt} />}
    </div>
  );
}

/** Статус лида: «В работе» кнопкой, отказные — выпадающим списком (их 17, кнопками не уместить);
 * отказ сразу пишется в Bitrix (`POST /api/leads/{id}/status`). «В работе» сначала открывает поле
 * «Следующая обработка» с датой по правилу Резерва (`GET /api/leads/{id}/next-work`): день, а если
 * нужно — и время; «В работу» пишет статус с датой. Лид уже «В работе» — та же кнопка меняет дату.
 * Один на «Чаты», «Обзвон» и панель расширения; `code` — статус лида сейчас.
 * Спеки `docs/specs/2026-10-06-lead-status-design.md`, `docs/specs/2026-10-20-lead-next-work-design.md`.
 * `onDeal` — между ними кнопка «Сделка»: перевод лида в сделку. Сама ничего не пишет — открывает
 * форму `CreateDeal` (стадия, дата, салон) у вызывающего; `dealOpen` — форма сейчас открыта. */
export function LeadStatusSwitch({
  leadId,
  code,
  chatId,
  onDeal,
  dealOpen = false,
}: {
  leadId: number;
  code: string | null;
  chatId: number | null;
  onDeal?: () => void;
  dealOpen?: boolean;
}) {
  const qc = useQueryClient();
  const statuses = useQuery({ queryKey: ["lead-statuses"], queryFn: loadLeadStatuses, staleTime: 60 * 60_000 });
  // Поле «Следующая обработка» открыто; дата — набранная, пока не тронули — рекомендация.
  const [planning, setPlanning] = useState(false);
  const [nextAt, setNextAt] = useState("");
  const advice = useQuery({
    queryKey: ["lead-next-work", leadId],
    queryFn: () => loadLeadNextWork(leadId),
    enabled: planning,
    staleTime: 0,
  });
  const change = useMutation({
    mutationFn: ({ status, next }: { status: string; next?: string }) =>
      setLeadStatus(leadId, status, chatId ?? undefined, next),
    onSuccess: () => {
      setPlanning(false);
      void qc.invalidateQueries({ queryKey: ["lead-next-work", leadId] });
      void qc.invalidateQueries({ queryKey: ["lead-card", leadId] });
      void qc.invalidateQueries({ queryKey: ["ext-peer"] });
      void qc.invalidateQueries({ queryKey: ["client-chats"] });
      void qc.invalidateQueries({ queryKey: ["search"] });
      if (chatId) {
        void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
        void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      }
    },
  });
  const list = statuses.data?.statuses ?? [];
  const work = list.find((s) => s.kind === "work");
  const refusals = list.filter((s) => s.kind === "refuse");
  // Пока идёт запись — показываем выбранное, а не старое из карточки.
  const now = (change.isPending && change.variables?.status) || code;
  const inRefuse = refusals.some((s) => s.code === now);
  const inWork = work !== undefined && now === work.code;
  // Своя дата не набрана — в поле рекомендация.
  const shown = nextAt || advice.data?.at || "";
  return (
    <div data-annot="статус лида">
      <div className="mb-1 empty:hidden">
        <BitrixPending
          lead={leadId}
          refreshKey={change.isSuccess ? change.submittedAt : 0}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ["lead-card", leadId] });
            if (chatId) void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
          }}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {work && (
          <button
            type="button"
            disabled={change.isPending}
            aria-pressed={planning}
            title={inWork ? "Лид в работе — поменять дату следующей обработки" : "Перевести в работу с датой следующей обработки"}
            onClick={() => {
              setNextAt("");
              setPlanning((was) => !was);
            }}
            className={`${chip} h-7 ${inWork || planning ? on : off}`}
          >
            {work.name}
          </button>
        )}
        {onDeal && (
          <button
            type="button"
            aria-pressed={dealOpen}
            title="Перевести лид в сделку: стадия, дата и салон — в форме ниже"
            onClick={onDeal}
            className={`${chip} h-7 ${dealOpen ? on : off}`}
            data-annot="лид в сделку"
          >
            Сделка
          </button>
        )}
        <select
          aria-label="Отказ — статус лида"
          title="Закрыть лид отказом — статус сразу запишется в Bitrix"
          value={inRefuse && now ? now : ""}
          disabled={refusals.length === 0 || change.isPending}
          onChange={(e) => e.target.value && change.mutate({ status: e.target.value })}
          className={`${input} min-w-0 flex-1 bg-bg ${inRefuse ? "border-accent text-err" : "text-fg-muted"}`}
        >
          <option value="" disabled>
            Отказ…
          </option>
          {refusals.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      {planning && work && (
        <div className="mt-1 flex flex-col gap-1" data-annot="следующая обработка лида">
          <DateTimePick label="Следующая обработка" value={shown} onChange={setNextAt} required placeholder="считаю…" />
          {advice.data && (
            <div className="text-[11.5px] leading-snug text-fg-muted">
              {inWork && advice.data.current && <>Сейчас: {dateTimeLabel(advice.data.current)}. </>}
              По правилу: {advice.data.rule}
              {nextAt && nextAt !== advice.data.at && (
                <>
                  {" · "}
                  <button type="button" className="underline hover:text-fg" onClick={() => setNextAt("")}>
                    вернуть
                  </button>
                </>
              )}
            </div>
          )}
          {advice.isError && <div className="text-err">Рекомендация не посчиталась: {advice.error.message}</div>}
          <div className="flex gap-1">
            <button
              type="button"
              disabled={change.isPending || !shown}
              onClick={() => change.mutate({ status: work.code, next: shown })}
              className={btn}
            >
              {inWork ? "Сохранить дату" : "В работу"}
            </button>
            <button type="button" onClick={() => setPlanning(false)} className={ghost}>
              отмена
            </button>
          </div>
        </div>
      )}
      {statuses.isError && <div className="mt-1 text-err">Статусы лида не загрузились: {statuses.error.message}</div>}
      {change.isPending && <div className="mt-1 text-fg-muted">Записываю в Bitrix…</div>}
      {change.isError && <div className="mt-1 text-err">{change.error.message}</div>}
      {change.isSuccess && change.data.next_at && (
        <div className="mt-1 text-fg-muted">Следующая обработка: {dateTimeLabel(change.data.next_at)}</div>
      )}
      {change.isSuccess && <Saved at={change.submittedAt} />}
    </div>
  );
}

/** Город сделки: поле «Салон» живого Bitrix первым (CRM отстаёт от правки из панели), иначе из CRM. */
/** Направление «Прокат» в Bitrix (`deal/stage.НАПРАВЛЕНИЕ`): город и мероприятие правятся только у него. */
const ПРОКАТ = 0;

export function dealCity(d: DealCard["deal"], locations: Location[]): { short: string; name: string } | null {
  const l = locations.find((x) => x.bitrix_id === d.location_bitrix_id);
  if (l) return { short: l.short || l.name, name: l.name };
  return d.city ? { short: d.city_short ?? d.city, name: d.city } : null;
}

/** Город сделки коротко («МСК»): щёлкнул — салоны с кассой в порядке CRM (`sortOrder`), выбрал —
 * сразу записано в поле «Салон» Bitrix (`POST /api/deals/{id}/location`), как «Изм.» во
 * всплывающем окне legacy. */
export function CityPick({
  deal,
  locations,
  chatId,
  big,
  compact = false,
}: {
  deal: DealCard["deal"];
  locations: Location[];
  chatId: number;
  big?: boolean;
  /** Кнопка встаёт в строку родителя (`contents`, панель расширения: «1254034 · СПБ ▾ … БРОНЬ»),
   *  список салонов и итог записи — строкой под ней (`basis-full order-last` в его `flex-wrap`). */
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const change = useMutation({
    mutationFn: (location: number) => setDealLocation(deal.id, location, chatId),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["deal-card", deal.id] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chats"] });
    },
  });
  const city = dealCity(deal, locations);
  const below = compact ? "basis-full order-last" : "";
  // Город правим только у «Проката»: сервер отвечает BAD_STAGE, а 30.10 по отказу жали 4 раза подряд.
  const чужая = (deal.category ?? 0) !== ПРОКАТ;
  return (
    // Разметчик меряет `data-annot` по рамке: у `contents` её нет — в тесном виде метка на кнопке.
    <div className={compact ? "contents" : big ? "" : "mt-1"} data-annot={compact ? undefined : "город сделки"}>
      <button
        type="button"
        aria-expanded={open}
        title={чужая ? "Город меняют только у сделок «Проката»" : city ? `${city.name} — изменить город` : "Указать город"}
        disabled={чужая || locations.length === 0 || change.isPending}
        onClick={() => setOpen(!open)}
        data-annot={compact ? "город сделки" : undefined}
        className={`font-semibold hover:text-fg disabled:cursor-default ${big ? "text-[20px] leading-tight" : compact ? "h-6 whitespace-nowrap text-[13px]" : "text-[13px]"} ${city ? "text-fg-muted" : "text-warn"}`}
      >
        {city?.short ?? "город не указан"}{" "}
        {!чужая && <span className="text-[11px] font-normal">{open ? "▴" : "▾"}</span>}
      </button>
      {open && (
        <div className={`mt-1 flex flex-wrap gap-1 ${below}`}>
          {locations.map((l) => (
            <button
              key={l.id}
              type="button"
              title={l.name}
              disabled={change.isPending || l.bitrix_id === deal.location_bitrix_id}
              onClick={() => change.mutate(l.id)}
              className={`${chip} ${l.bitrix_id === deal.location_bitrix_id ? on : off}`}
            >
              {l.short || l.name}
            </button>
          ))}
        </div>
      )}
      {change.isPending && <div className={`mt-1 text-fg-muted ${below}`}>Записываю в Bitrix…</div>}
      {change.isError && <div className={`mt-1 text-err ${below}`}>{change.error.message}</div>}
      {change.isSuccess && <Saved at={change.submittedAt} className={below} />}
    </div>
  );
}

/** Мероприятие и его дата — список и поле-дата сделки Bitrix: выбрал — сразу записано
 * (`POST /api/deals/{id}/event`), чтобы не заполнять то же самое в портале. */
export function EventPick({
  deal,
  events,
  chatId,
  compact = false,
}: {
  deal: DealCard["deal"];
  events: DealEvent[];
  chatId: number;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const ids = useId();
  const change = useMutation({
    mutationFn: (p: { event?: number; date?: string }) => setDealEvent(deal.id, p, chatId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deal-card", deal.id] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
    },
  });
  // Пока идёт запись — показываем выбранное, а не старое из карточки.
  const event = (change.isPending && change.variables?.event) || deal.event_id;
  const чужая = (deal.category ?? 0) !== ПРОКАТ;
  const select = (
    <select
      id={`${ids}-event`}
      aria-label="Мероприятие"
      value={event && events.some((e) => e.id === event) ? event : ""}
      disabled={чужая || events.length === 0 || change.isPending}
      title={чужая ? "Мероприятие меняют только у сделок «Проката»" : undefined}
      onChange={(e) => e.target.value && change.mutate({ event: Number(e.target.value) })}
      className={`${input} bg-bg ${compact ? "w-full min-w-0" : "min-w-[120px] flex-1"} ${event ? "" : "text-fg-muted"}`}
    >
      <option value="" disabled>
        мероприятие
      </option>
      {events.map((e) => (
        <option key={e.id} value={e.id}>
          {e.name}
        </option>
      ))}
    </select>
  );
  // Свой календарь вместо `input type="date"`: «6 октября», а не дата по локали браузера; значение —
  // строка `ГГГГ-ММ-ДД` без пояса. Пока идёт запись — показываем выбранный день.
  const date = (
    <DatePick
      id={`${ids}-date`}
      label="Дата мероприятия"
      value={(change.isPending && change.variables?.date) || deal.event_date || ""}
      disabled={чужая || change.isPending}
      placeholder="выбрать…"
      align={compact ? "right" : "left"}
      onChange={(day) => change.mutate({ date: day })}
    />
  );
  return (
    <div className={compact ? "" : "mt-2"} data-annot="мероприятие">
      {compact ? (
        // Две колонки с подписями над полями: «дата» без подписи читалась как неизвестно чья дата.
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-1.5">
          <label htmlFor={`${ids}-event`} className={caption}>
            мероприятие
          </label>
          <label htmlFor={`${ids}-date`} className={caption}>
            дата мероприятия
          </label>
          {select}
          {date}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          {select}
          <label className="flex items-center gap-1 text-fg-muted" title="Дата мероприятия">
            дата
            {date}
          </label>
        </div>
      )}
      {change.isPending && <div className="mt-1 text-fg-muted">Записываю в Bitrix…</div>}
      {change.isError && <div className="mt-1 text-err">{change.error.message}</div>}
      {change.isSuccess && <Saved at={change.submittedAt} />}
    </div>
  );
}

/** Мелкая служебная строка блока сделки и ссылки в ней; цели клика — не ниже 24 px. */
const tiny = "text-[11.5px] leading-none text-fg-muted";
const tinyLink = "inline-flex h-6 items-center gap-0.5 whitespace-nowrap text-accent-text hover:underline";

/**
 * Сделка коротко — ОДИН блок на панель «Чатов» и панель расширения Chrome (владелец 05.10.26:
 * «чтобы одинаковый вид был, что в чате, что в расширении»):
 *   1254034 ↗ · СПБ ▾                 БРОНЬ      — номер (карточка в кабинете), город, стадия коротко
 *   сделка · Bitrix ↗ · CRM ↗
 *   [В работе] [Ждем] [Думают] [Отказ ▾]
 *   следующая обработка / запись
 *   мероприятие · дата мероприятия
 *   последний комментарий · + комментарий
 * `base` — адрес кабинета для ссылок (расширение живёт на чужой странице); `children` — что добавить
 * под блоком своего у экрана (у «Чатов» — даты проката). `chatId` — журнал действий чата; чата ещё
 * нет (расширение) — `null`, действие всё равно запишется. `quickComment` — поле комментария
 * открыто сразу (панель «Доставок»).
 */
export function DealBrief({
  dealId,
  chatId,
  base = "",
  quickComment = false,
  visit = false,
  children,
}: {
  dealId: number;
  chatId: number | null;
  base?: string;
  quickComment?: boolean;
  /** Повторка в блоке сделки — только в расширении: в «Чатах» она на вкладке «Договор». */
  visit?: boolean;
  children?: (card: DealCard) => React.ReactNode;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["deal-card", dealId], queryFn: () => loadDealCard(dealId), staleTime: DEAL_CARD_STALE });
  const stages = useStages();
  const chat = chatId ?? 0;
  const number = (
    <a
      className="inline-flex h-6 items-center gap-1 whitespace-nowrap font-mono text-[13px] font-medium text-accent-text hover:underline"
      href={`${base}/deals/${dealId}`}
      target="_blank"
      rel="noreferrer"
      title={`Сделка ${dealId} — карточка в кабинете`}
    >
      {dealId} <ExternalLink size={12} aria-hidden />
    </a>
  );
  // Сделка в Bitrix и в CRM — мелкие ссылки под номером, не кнопки: место нужно полям сделки.
  // Переход — в журнал действий чата; сбой журнала переходу не мешает.
  const opened = (action: "open_bitrix" | "open_crm") => () =>
    void (
      chatId &&
      logChatAction(chatId, action, dealId).then(
        () => qc.invalidateQueries({ queryKey: ["chat-actions", chatId] }),
        () => undefined,
      )
    );
  const crm = navObject("dealID")?.items.find((i) => i.sys === "crm");
  // Очередь записей в Bitrix: правка не дошла — дошлёт сервер; дошла — перечитать карточку.
  // Видна и когда карточка не загрузилась: Bitrix лежит — как раз тогда.
  const band = (
    <BitrixPending
      deal={dealId}
      refreshKey={q.dataUpdatedAt + q.errorUpdatedAt}
      onDone={() => void qc.invalidateQueries({ queryKey: ["deal-card", dealId] })}
    />
  );
  const open = (
    <div className={`${tiny} flex basis-full items-center gap-x-1.5`} data-annot="открыть сделку">
      сделка
      <span aria-hidden>·</span>
      <a className={tinyLink} href={bitrixUrl("deal", dealId)} target="_blank" rel="noreferrer" title={`Открыть сделку ${dealId} в Bitrix`} onClick={opened("open_bitrix")}>
        Bitrix <ExternalLink size={10} aria-hidden />
      </a>
      {crm && (
        <>
          <span aria-hidden>·</span>
          <a className={tinyLink} href={navUrl(crm, "dealID", dealId)} target="_blank" rel="noreferrer" title={`Открыть сделку ${dealId} в CRM`} onClick={opened("open_crm")}>
            CRM <ExternalLink size={10} aria-hidden />
          </a>
        </>
      )}
    </div>
  );
  if (q.isPending) return <div className="flex flex-col gap-1.5">{band}<div className="flex flex-wrap items-center gap-x-2">{number}<span className="text-[12.5px] text-fg-muted">загружаю…</span>{open}</div></div>;
  if (q.isError) return <div className="flex flex-col gap-1.5">{band}<div className="flex flex-wrap items-center gap-x-2">{number}<span className="text-[12.5px] text-err">{q.error.message}</span>{open}</div></div>;
  const d = q.data.deal;
  const list = stages.data?.stages ?? [];
  const stage = list.find((s) => s.code === d.stage_code);
  const kind = stage?.kind ?? "other";
  return (
    <div className="flex flex-col gap-1.5 text-[12.5px]" data-annot="сделка">
      {/* Одна строка: номер · город … стадия; ниже — ссылки. Список салонов `CityPick` и итог его
          записи встают строкой под ними (`compact`). */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        {number}
        <span aria-hidden className="text-fg-faint">·</span>
        <CityPick deal={d} locations={stages.data?.locations ?? []} chatId={chat} compact />
        <span className={`ml-auto min-w-0 truncate font-semibold uppercase tracking-wide ${STAGE_COLOR[kind]}`} title={d.stage ?? undefined} data-annot="стадия">
          {stage?.short || d.stage || "—"}
        </span>
        {open}
      </div>
      {band}
      <StageSwitch deal={d} stages={list} chatId={chat} compact />
      <WaitDate deal={d} chatId={chat} isWait={kind === "wait"} compact />
      <EventPick deal={d} events={stages.data?.events ?? []} chatId={chat} compact />
      <CommentAdd dealId={d.id} events={q.data.events} chatId={chat} quick={quickComment} />
      {/* Повторная примерка — только у брони; спека `docs/specs/2026-10-22-deal-visit-edit-design.md`. */}
      {visit && d.stage_code === "4" && <VisitBlock dealId={d.id} chatId={chat} />}
      {children?.(q.data)}
    </div>
  );
}

/** Лента карточки сделки (лида без сделки) — та же в «Чатах» и в панели расширения: узкий вид,
 * новое сверху, отбор по типам события выпадающим списком, по умолчанию всё.
 * Спека `docs/specs/2026-10-05-ext-timeline-design.md`. */
export function DealHistory({ title, events, errors, base = "" }: { title: string; events: CardEvent[]; errors: string[]; base?: string }) {
  return (
    <section className="flex flex-col gap-1.5" data-annot="история">
      <h2 className="text-[12.5px] font-medium">{title}</h2>
      {errors.length > 0 && <p className="text-[12px] leading-snug text-warn">История неполная: {errors.join("; ")}</p>}
      <Timeline events={events} newestFirst compact filter="kinds" base={base} />
    </section>
  );
}

/** Сделка в панели «Чатов». `view` — вкладка панели: «Сделка» — общий блок `DealBrief`, под ним
 * `slot` (кнопки записи, как в расширении) и строка салона; «История» — лента. Обе части
 * смонтированы, неактивная скрыта: недописанный комментарий вкладку переживает. */
function Deal({ dealId, chatId, view, slot }: { dealId: number; chatId: number; view: PanelView; slot: React.ReactNode }) {
  const q = useQuery({ queryKey: ["deal-card", dealId], queryFn: () => loadDealCard(dealId), staleTime: DEAL_CARD_STALE });
  const c = q.data;
  const sl = c?.salon;
  return (
    <>
      <div hidden={view !== "deal"} className="flex flex-col gap-2 p-3">
        <div className={card}>
          <DealBrief dealId={dealId} chatId={chatId}>
            {({ deal: d, salon, lead }) => (
              <>
                <RentDates deal={d} salon={salon} chatId={chatId} big={RENT_STAGES.has(d.stage_code ?? "")} />
                {/* Чего нет в блоке: переходы в соседние системы. Кто ведёт и когда создана — в «Истории». */}
                <div className="flex flex-wrap gap-x-3">
                  <NavMenu r={{ obj: "dealID", id: d.id }} />
                  {lead && <NavMenu r={{ obj: "leadID", id: lead.id }} />}
                </div>
              </>
            )}
          </DealBrief>
        </div>
        {slot}
        {c && c.errors.length > 0 && (
          <div className="rounded-[var(--radius-card)] border border-warn p-2 text-[12.5px] text-warn">
            Карточка неполная: {c.errors.join("; ")}
          </div>
        )}
        {/* Салон одной строкой; договор, товары и оплата — на вкладке «Договор». */}
        {sl && (sl.visits > 0 || sl.issued > 0 || sl.returned > 0) && (
          <div className="px-1 text-[12.5px] text-fg-muted" data-annot="салон">
            Приходов в салон <span className="text-fg">{sl.visits}</span>
            {sl.repeat_visits > 0 && ` · повторных примерок ${sl.repeat_visits}`}
            {(sl.issued > 0 || sl.returned > 0) && (
              <>
                {" · выдано "}
                <span className="text-fg">{sl.issued}</span>
                {" / возвращено "}
                <span className="text-fg">{sl.returned}</span>
                {sl.fact_start && ` (${dt(sl.fact_start)} – ${sl.fact_end ? dt(sl.fact_end) : "на руках"})`}
              </>
            )}
          </div>
        )}
      </div>
      <div hidden={view !== "history"} className="p-3">
        {c ? (
          <DealHistory title="История сделки" events={c.events} errors={c.errors} />
        ) : (
          <div className="text-[12.5px] text-fg-muted">{q.isError ? `История не загрузилась: ${q.error.message}` : "Загрузка — собираю историю…"}</div>
        )}
      </div>
    </>
  );
}

/** Дата записи / следующей обработки — всегда на виду: щёлкнул, выбрал день, набрал время,
 * Enter — и дата записана в Bitrix (`POST /api/deals/{id}/wait`), стадия прежняя. Пояса нет:
 * какие часы набрали, такие и уходят. */
export function WaitDate({
  deal,
  chatId,
  isWait,
  compact = false,
}: {
  deal: DealCard["deal"];
  chatId: number;
  isWait: boolean;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const change = useMutation({
    mutationFn: (value: string) => setDealWait(deal.id, value, chatId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deal-card", deal.id] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chats"] });
    },
  });
  // Пока идёт запись — показываем набранное, а не старую дату из карточки.
  const value = change.isPending && change.variables ? change.variables : toLocalInput(deal.visit);
  const label = isWait ? "Запись" : "Следующая обработка";
  return (
    <div className={compact ? "" : "mt-2"} data-annot="дата обработки">
      <DateTimePick
        label={compact ? label.toLowerCase() : label}
        value={value}
        onChange={(v) => v && v !== toLocalInput(deal.visit) && change.mutate(v)}
        required
        placeholder="не назначена — выбрать"
        compact={compact}
      />
      {change.isPending && <div className="mt-1 text-fg-muted">Записываю в Bitrix…</div>}
      {change.isError && <div className="mt-1 text-err">{change.error.message}</div>}
      {change.isSuccess && <Saved at={change.submittedAt} />}
    </div>
  );
}

/** Последний комментарий сделки и «+ комментарий» — как во всплывающем окне legacy: заметка
 * сотрудника с его именем уходит в очередь CRM (`POST /api/deals/{id}/comment`), та за минуту
 * отправляет её в ленту сделки Bitrix и показывает салону.
 * Ctrl+Enter — сохранить. Только что записанный показываем сразу, не дожидаясь карточки.
 * `quick` — поле открыто всегда и пишет по Enter (Shift+Enter — новая строка): набрал и ушёл. */
export function CommentAdd({
  dealId,
  events,
  chatId,
  quick = false,
}: {
  dealId: number;
  events: CardEvent[];
  chatId: number;
  quick?: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: () => addDealComment(dealId, text, chatId),
    onSuccess: () => {
      setText("");
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["deal-card", dealId] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
    },
  });
  const [all, setAll] = useState(false);
  // Комментарий — слова сотрудника. Записи CRM о правке полей сделки (`auto`: «… изменил Начало
  // проката…») сюда не идут — они в «Истории».
  const last = events.filter((e) => e.kind === "comment" && e.text && !e.auto).at(-1);
  // Карточка могла ещё не перечитаться — свежий комментарий берём из ответа записи: там он «Имя:
  // текст», а в ленте автор уже отдельно.
  const fresh =
    add.isSuccess &&
    !events.some((e) => e.kind === "comment" && e.text && (e.text === add.data.text || add.data.text.endsWith(`: ${e.text}`)));
  const shown = fresh ? add.data.text : (last?.text ?? "");
  // Откуда комментарий: из CRM (салон и КЦ — карточка клиента, окна CRM) или из ленты сделки Bitrix.
  const from = fresh ? null : last?.src === "crm" ? "CRM" : last ? "Bitrix" : null;
  const head = fresh ? ["сейчас"] : last ? [dt(last.at), last.who, from] : [];
  const long = shown.length > 400 || shown.split("\n").length > 8;
  const save = () => text.trim() && !add.isPending && add.mutate();
  return (
    <div className="flex flex-col gap-1" data-annot="комментарий">
      <div className="flex flex-wrap items-center gap-x-1.5">
        <span
          className="min-w-0 flex-1 text-[11.5px] leading-snug text-fg-muted"
          title={from === "CRM" ? "Комментарий записан в CRM — салоном или КЦ" : from ? "Комментарий из ленты сделки Bitrix" : undefined}
        >
          последний комментарий{head.filter(Boolean).map((x) => ` · ${x}`)}
        </span>
        {!open && !quick && (
          <button
            type="button"
            className="inline-flex h-6 shrink-0 items-center whitespace-nowrap text-accent-text hover:underline"
            title="Добавить комментарий к сделке — в CRM и Bitrix"
            onClick={() => (add.reset(), setOpen(true))}
          >
            + комментарий
          </button>
        )}
      </div>
      {/* Текст целиком, с переносами автора; простыню сворачиваем, но одной кнопкой она вся. */}
      {shown ? (
        <p className={`whitespace-pre-wrap break-words leading-snug ${long && !all ? "line-clamp-6" : ""}`}>{shown}</p>
      ) : (
        <p className="text-fg-muted">комментариев нет</p>
      )}
      {long && (
        <button type="button" className={`${tinyLink} self-start`} onClick={() => setAll((v) => !v)}>
          {all ? "свернуть" : "показать полностью"}
        </button>
      )}
      {(open || quick) && (
        <form className="flex flex-col gap-1" onSubmit={(e) => (e.preventDefault(), save())}>
          <textarea
            autoFocus={!quick}
            rows={quick ? 2 : 3}
            maxLength={480}
            placeholder={quick ? "Комментарий к сделке — Enter, и он записан; увидят КЦ и салон" : "Комментарий к сделке — увидят КЦ и салон, с вашим именем"}
            aria-label="Комментарий к сделке"
            className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-transparent px-2 py-1 leading-snug hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              const fast = quick && !e.shiftKey && !e.nativeEvent.isComposing;
              if (e.key === "Enter" && (fast || e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                save();
              }
              if (e.key === "Escape") setOpen(false);
            }}
          />
          <div className="flex items-center gap-2">
            <button type="submit" className={btn} disabled={!text.trim() || add.isPending} title={quick ? "Enter" : "Ctrl+Enter"}>
              {add.isPending ? "Записываю…" : "Сохранить"}
            </button>
            {!quick && (
              <button type="button" className={ghost} disabled={add.isPending} onClick={() => setOpen(false)}>
                Отмена
              </button>
            )}
          </div>
        </form>
      )}
      {add.isError && <div role="alert" className="text-err">{add.error.message}</div>}
      {add.isSuccess && !open && (
        <div role="status" className="rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1 leading-snug text-ok">
          ✓ Комментарий записан в {new Date(add.submittedAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })} — в Bitrix и CRM появится в течение минуты
        </div>
      )}
    </div>
  );
}

/** Даты проката сделки (поля Bitrix, их же пишет CRM при договоре): начало и конец — два поля-даты,
 * крупные у брони и выдачи. Выбрал день — сразу записано (`POST /api/deals/{id}/rent`); вторая дата
 * стоит на месте, а оказалась по другую сторону выбранной (или не задана) — встаёт на тот же день.
 * `big` не задан — крупно по стадии сделки (бронь и выдача). */
export function RentDates({
  deal,
  salon,
  chatId,
  big = RENT_STAGES.has(deal.stage_code ?? ""),
}: {
  deal: DealCard["deal"];
  salon: DealCard["salon"];
  chatId: number;
  big?: boolean;
}) {
  const qc = useQueryClient();
  const change = useMutation({
    mutationFn: (p: { start: string; end: string }) => setDealRent(deal.id, p.start, p.end, chatId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deal-card", deal.id] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
    },
  });
  const has = Boolean(deal.rent_start || deal.rent_end);
  if (!big && !has) return null;
  // Пока идёт запись — показываем выбранное, а не старое из карточки.
  const sent = change.isPending ? change.variables : undefined;
  const start = sent?.start ?? deal.rent_start?.slice(0, 10) ?? "";
  const end = sent?.end ?? deal.rent_end?.slice(0, 10) ?? "";
  const days = rentDays(start || null, end || null);
  const contractDiffers =
    salon?.contract_start != null && (salon.contract_start !== deal.rent_start || salon.contract_end !== deal.rent_end);
  return (
    <div className="mt-3" data-annot="даты проката">
      <div className={big ? "mb-1 text-[11px] uppercase tracking-wide text-fg-muted" : caption}>
        Прокат{days ? ` · ${days} дн.` : ""}
        {big && !has && <span className="normal-case tracking-normal text-warn"> · даты не заданы</span>}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-1.5">
        <DatePick
          label="Начало проката"
          value={start}
          big={big}
          disabled={change.isPending}
          placeholder="начало…"
          onChange={(d) => change.mutate({ start: d, end: end && end >= d ? end : d })}
        />
        <span aria-hidden className="text-fg-muted">–</span>
        <DatePick
          label="Конец проката"
          value={end}
          big={big}
          disabled={change.isPending}
          placeholder="конец…"
          align="right"
          onChange={(d) => change.mutate({ start: start && start <= d ? start : d, end: d })}
        />
      </div>
      {contractDiffers && salon && (
        <div className="text-warn">
          По договору CRM {day(salon.contract_start)} – {day(salon.contract_end)} — отличается от сделки
        </div>
      )}
      {change.isPending && <div className="mt-1 text-fg-muted">Записываю в Bitrix…</div>}
      {change.isError && <div className="mt-1 text-err">{change.error.message}</div>}
      {change.isSuccess && <Saved at={change.submittedAt} />}
    </div>
  );
}

/** Вкладка «ЛК»: что сотрудники делали с этим чатом и сделкой из кабинета — журнал
 * `user_action`, новые сверху. */
function Actions({ chatId, dealId, stages }: { chatId: number; dealId: number | null; stages: Stage[] }) {
  const q = useQuery({
    queryKey: ["chat-actions", chatId, dealId],
    queryFn: () => loadChatActions(chatId, dealId ?? undefined),
    staleTime: 30_000,
  });
  const rows = q.data?.actions ?? [];
  const name = (code: unknown) => stages.find((s) => s.code === code)?.name ?? String(code ?? "—");
  return (
    <div className={card} data-annot="действия в кабинете">
      <div className="mb-1 text-[13px] font-medium">Действия в кабинете</div>
      {q.isPending && <div className="text-fg-muted">Загрузка…</div>}
      {q.isError && <div className="text-err">Журнал не загрузился: {q.error.message}</div>}
      {q.isSuccess && rows.length === 0 && <div className="text-fg-muted">С этим чатом в кабинете ещё ничего не делали.</div>}
      <ul className="flex flex-col gap-0.5">
        {rows.map((a) => (
          <li key={a.id} className={a.error ? "text-err" : ""}>
            <span className="text-fg-muted">{dt(a.at)}</span> {a.user || "—"} — {describeAction(a, name)}
            {a.error && <span> · {a.error}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Lead({
  leadId,
  chatId,
  view,
  slot,
  onDeal,
}: {
  leadId: number;
  chatId: number;
  view: PanelView;
  slot: React.ReactNode;
  onDeal: (dealId: number) => void;
}) {
  const q = useQuery({ queryKey: ["lead-card", leadId], queryFn: () => loadLeadCard(leadId), staleTime: DEAL_CARD_STALE });
  const redirect = q.data?.redirect ?? null;
  // `onDeal` в зависимостях нет намеренно: это свежая стрелка на каждый рендер.
  useEffect(() => void (redirect !== null && onDeal(redirect)), [redirect]);
  if (q.isPending) return <div className={note}>Собираю лид {leadId}…</div>;
  if (q.isError) return <div className={`${note} text-err`}>Не удалось открыть лид {leadId}: {q.error.message}</div>;
  const c = q.data;
  if (c.redirect) return <Deal dealId={c.redirect} chatId={chatId} view={view} slot={slot} />;
  return (
    <>
      <div hidden={view !== "deal"} className="flex flex-col gap-2 p-3">
        {/* Шапка — как у сделки: номер (карточка в кабинете) слева, статус справа. */}
        <div className={`${card} flex flex-col gap-1.5`} data-annot="лид">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <Link to={`/leads/${c.lead.id}`} className="inline-flex h-6 items-center gap-1 whitespace-nowrap text-[13px] font-medium text-accent-text hover:underline" title={`Лид ${c.lead.id} — карточка в кабинете`}>
              Лид <span className="font-mono">{c.lead.id}</span>
            </Link>
            <span className="ml-auto min-w-0 truncate font-semibold uppercase tracking-wide">{c.lead.status ?? "—"}</span>
            <div className={`${tiny} basis-full`}>сделки нет — её создаст запись клиента или форма выше</div>
          </div>
          <LeadStatusSwitch leadId={c.lead.id} code={c.lead.status_code ?? null} chatId={chatId} />
          <div>{c.lead.title}</div>
          <NavMenu r={{ obj: "leadID", id: c.lead.id }} />
          <div className="text-fg-muted">
            {c.lead.source ?? "—"} · {c.lead.assigned ?? "—"} · создан {dt(c.lead.created)}
          </div>
        </div>
        {slot}
      </div>
      <div hidden={view !== "history"} className="p-3">
        <DealHistory title="История лида" events={c.events ?? []} errors={c.errors ?? []} />
      </div>
    </>
  );
}
